import { useState, useContext } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { getListKeyPersonnels } from "actions/RFQ/Supplier/rfqActions";
import {
	SmartFieldQuestionnaireBase,
	filterSmartFieldOptionForSubmittedRFQ,
} from "./SmartFieldQuestionnaireBase";
import { useSmartFieldSelection } from "../hooks/useSmartFieldSelection";
import {
	KeyPersonnel,
	KeyPersonnelSkeleton,
} from "pages/RFQ/RFQ/CreateRFQ/PreviewRFQ/Questionnaire/_components/KeyPersonnel";
import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";

const keyPersonnelDummyData = {
	id: 0,
	first_name: "Example",
	last_name: "Person Card",
	position: "Their Position",
	projects_completed: [
		{
			name: "Their Last Project",
		},
	],
};

export const SmartFieldKeyPersonnel = ({
	className,
	question,
	required,
	isClientPreview,
	answer,
	...props
}) => {
	const [search, setSearch] = useState("");
	const [selectedFilter, setSelectedFilter] = useState([]);
	const companyId = useSelector((state) => state?.search?.activeCompany?.id);
	const { rfxId, isClientView } = useRfxSubmissionData();
	const { value, error, touched, isSelected, selectHandler, isSubmitting } =
		useSmartFieldSelection(props, { shouldSubmit: true });

	// Prepare query parameters
	const queryParams = {
		search: search,
		filters: selectedFilter,
		replacement: 1,
		rfq_id: rfxId,
	};

	const useAnswerData =
		(props?.disabled && answer?.selectedKeyPersonnel) || !rfxId || isClientView;

	// Fetch key personnel data (for list)
	const {
		data,
		isLoading,
		error: queryError,
	} = useQuery({
		queryKey: ["keyPersonnel", queryParams],
		queryFn: getListKeyPersonnels,

		enabled: !useAnswerData,
	});

	// if the key_personnels is loaded already, use that (as we may have just transitioned from edit to view)
	const keyPersonnels =
		data?.key_personnels ?? answer?.selectedKeyPersonnel ?? [];
	const filteringList = data?.filters || [];

	// Filter data for preview mode
	const filteredData = filterSmartFieldOptionForSubmittedRFQ(
		keyPersonnels,
		value,
		props?.disabled
	);

	const selectedIds = value || [];
	const selectedData = keyPersonnels.filter((person) =>
		selectedIds.includes(person.id)
	);

	if (isClientPreview) {
		filteredData.push(keyPersonnelDummyData);
	}

	return (
		<SmartFieldQuestionnaireBase
			className={className}
			question={question}
			required={required}
			description="Please choose key personnel that might work on this project."
			searchPlaceholder="Search name, position or project"
			isLoading={isLoading}
			filteringList={filteringList}
			selectedFilter={selectedFilter}
			setSelectedFilter={setSelectedFilter}
			setSearch={setSearch}
			error={error}
			touched={touched}
			type="key-personnel"
			selectedData={selectedData}
			showFilter={!props?.disabled}
			showSearch={!props?.disabled}
			isClientPreview={isClientPreview}
			{...props}
		>
			{isLoading ? (
				[...Array(3)].map((_, index) => <KeyPersonnelSkeleton key={index} />)
			) : queryError ? (
				<div className="text-red-500">
					Error loading personnel: {queryError.message || "Unknown error"}
				</div>
			) : filteredData.length === 0 ? (
				<div className="italic text-gray-500">
					No personnel {useAnswerData ? "selected" : "found"}
				</div>
			) : (
				filteredData.map((personnel) => (
					<KeyPersonnel
						key={personnel.id}
						companyId={companyId}
						values={personnel}
						rfqSubmission={true}
						selected={isSelected(personnel?.id)}
						selectHandler={() => selectHandler(personnel?.id)}
						isPreview={props?.disabled}
						isClientPreview={isClientPreview}
						supplierSmartField
						isSubmitting={isSubmitting}
					/>
				))
			)}
		</SmartFieldQuestionnaireBase>
	);
};
